import { useI18n } from '@ecom/i18n';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';

import { SkIcon } from '@/components/ui/SkIcon';
import type { OrdersByStatusEntry } from '@/features/analytics/types';
import { STATUS_LABEL_KEY } from '@/features/orders/components/OrderStatusBadge';
import { FINISHED_STATUSES, STATUS_TONE_FIGMA, TONE_FILL } from '@/features/orders/status-tone';
import type { OrderStatus } from '@/features/orders/types';

type Filter = 'all' | 'open' | 'finished';

/** Statuses in the order the work goes, as in the design: received, prepared, on its way, delivered, then the rest. */
const FLOW: readonly OrderStatus[] = [
  'ORDER_CREATED',
  'PAYMENT_PENDING',
  'PAYMENT_CONFIRMED',
  'PROCESSING',
  'PACKED',
  'SHIPPED',
  'DELIVERED',
  'DELIVERY_FAILED',
  'PAYMENT_FAILED',
  'RETURN_REQUESTED',
  'RETURNED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'CANCELLED',
];

/**
 * « État des commandes » in the Figma design (« Souk 2.0 »): every order since the shop opened, by status, from
 * `GET /merchant/analytics` (`ordersByStatus`). A segmented filter narrows it to the orders still in progress or the
 * finished ones; the total, the stacked bar and the list follow the filter. Each status opens the order list filtered
 * on it; « Tout voir » opens the full list. Only statuses that hold orders are listed — the bar is their real share.
 */
export function OrderStatusPanel({
  entries,
}: {
  entries: readonly OrdersByStatusEntry[];
}): ReactElement {
  const { t } = useI18n();
  const [filter, setFilter] = useState<Filter>('all');

  const counts = new Map(entries.map((entry) => [entry.status, entry.count]));
  const rows = FLOW.filter((status) => (counts.get(status) ?? 0) > 0)
    .filter((status) =>
      filter === 'all'
        ? true
        : filter === 'finished'
          ? FINISHED_STATUSES.has(status)
          : !FINISHED_STATUSES.has(status),
    )
    .map((status) => ({ status, count: counts.get(status) ?? 0 }));
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  const filters: readonly { id: Filter; label: string }[] = [
    { id: 'all', label: t('merchant.dashboard.ordersFilterAll') },
    { id: 'open', label: t('merchant.dashboard.ordersFilterOpen') },
    { id: 'finished', label: t('merchant.dashboard.ordersFilterFinished') },
  ];

  return (
    <section
      className="sk-panel sk-orders-panel flex h-full flex-col"
      aria-labelledby="order-status-title"
    >
      <div className="sk-section-head">
        <div>
          <span className="sk-eyebrow">{t('merchant.dashboard.ordersEyebrow')}</span>
          <h2 id="order-status-title">{t('merchant.dashboard.ordersTitle')}</h2>
        </div>
        <Link to="/orders" className="sk-btn sk-btn--ghost">
          {t('merchant.dashboard.viewAll')}{' '}
          <SkIcon name="arrow" size={15} className="rtl:-scale-x-100" />
        </Link>
      </div>

      <div
        className="sk-filter-pills"
        role="group"
        aria-label={t('merchant.dashboard.ordersTitle')}
      >
        {filters.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={filter === item.id}
            onClick={() => {
              setFilter(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="sk-status-total">
        <strong>{total}</strong>
        <span>{t('merchant.dashboard.ordersTotal')}</span>
      </div>

      <div className="sk-stacked" aria-hidden="true">
        {rows.map((row, index) => (
          <i
            key={row.status}
            className={TONE_FILL[STATUS_TONE_FIGMA[row.status]]}
            style={{ flex: row.count, animationDelay: `${String(index * 50)}ms` }}
          />
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[var(--sk-line-strong)] px-4 py-3 text-sm text-inkSoft">
          {t('merchant.dashboard.statusBreakdown.emptyDescription')}
        </p>
      ) : (
        <ul className="sk-status-list">
          {rows.map((row) => (
            <li key={row.status}>
              <Link to={`/orders?status=${row.status}`}>
                <i className={TONE_FILL[STATUS_TONE_FIGMA[row.status]]} aria-hidden="true" />
                <span className="sk-status-name">{t(STATUS_LABEL_KEY[row.status])}</span>
                <strong>{row.count}</strong>
                <SkIcon name="chevron" size={13} className="text-inkFaint rtl:-scale-x-100" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
